import styled from '@emotion/styled';
import { Link, Typography } from '@mui/material';
import { shouldForwardProps } from '../utils';

const DisplayExtraLarge = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 400;
    font-size: 150px;
    line-height: 120%;
`;

const DisplayLarge = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 57px;
    line-height: 64px;
    letter-spacing: -0.25px;
`;

const DisplayMedium = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 45px;
    line-height: 52px;
`;

const DisplaySmall = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 36px;
    line-height: 44px;
`;

const HeadlineLarge = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 32px;
    line-height: 40px;
`;

const HeadlineMedium = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 28px;
    line-height: 36px;
`;

const HeadlineSmall = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
`;

const TitleLarge = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 600;
    font-size: 22px;
    line-height: 28px;
`;

const TitleLargeMedium = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 600;
    font-size: 20px;
    line-height: 26px;
`;

const TitleMedium = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
`;

const TitleSmall = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TitleSmallBold = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 700;
    font-size: 16px;
    line-height: 22px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LabelLargeBold = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    text-decoration: none;
    letter-spacing: 0.1px;
`;

const LabelLarge = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    text-decoration: none;
    letter-spacing: 0.1px;
`;

const LabelLargeLink = styled(Link, shouldForwardProps)<{ color?: string; isBold?: boolean }>`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: ${({ isBold }) => (isBold ? 700 : 500)};
    font-size: 14px;
    line-height: 20px;
    text-decoration: none;
    letter-spacing: 0.1px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ theme, color }) => color ?? theme.colors.onSurface};
    cursor: pointer;
`;

const LabelMedium = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.5px;
`;

const LabelSmall = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.5px;
`;

const BodyLarge = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.5px;
`;

const BodyLargeLink = styled(Link, shouldForwardProps)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    text-decoration: none;
    letter-spacing: 0.5px;
`;

const BodyMedium = styled(Typography)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    text-transform: none;
    letter-spacing: 0.25px;
`;

const BodyMediumLink = styled(Link, shouldForwardProps)<{ withUnderline?: boolean }>`
    text-decoration: ${({ withUnderline }) => (withUnderline ? 'underline' : 'none')};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    text-transform: none;
    letter-spacing: 0.25px;
`;

const BodySmall = styled(Typography, shouldForwardProps)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
`;

const BodySmallLink = styled(Link, shouldForwardProps)<{ withUnderline?: boolean }>`
    text-decoration: ${({ withUnderline }) => (withUnderline ? 'underline' : 'none')};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    text-transform: none;
    letter-spacing: 0.4px;
`;

const BodyTiny = styled(Typography, shouldForwardProps)`
    font-family: ${({ theme }) => theme.fonts.primary};
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
`;

const ButtonLabel = BodyMedium;

const ButtonLabelAfterIcon = styled(ButtonLabel)`
    padding: 12px;
`;

export {
    BodyLarge,
    BodyLargeLink,
    BodyMedium,
    BodyMediumLink,
    BodySmall,
    BodySmallLink,
    BodyTiny,
    ButtonLabel,
    ButtonLabelAfterIcon,
    DisplayExtraLarge,
    DisplayLarge,
    DisplayMedium,
    DisplaySmall,
    HeadlineLarge,
    HeadlineMedium,
    HeadlineSmall,
    LabelLarge,
    LabelLargeLink,
    LabelMedium,
    LabelSmall,
    TitleLarge,
    TitleLargeMedium,
    TitleMedium,
    TitleSmall,
    TitleSmallBold
};
